import {
  collection,
  doc,
  getDocs,
  limit,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from './firebase';
import type { UserNotification, UserNotificationPrefs } from './notificationTypes';
import { DEFAULT_NOTIFICATION_PREFS } from './notificationTypes';
import { safeParseDate } from './utils';

const USER_NOTIFICATION_LIMIT = 40;

export function parseNotificationPrefs(raw: unknown): UserNotificationPrefs {
  if (!raw || typeof raw !== 'object') return DEFAULT_NOTIFICATION_PREFS;
  const data = raw as Partial<UserNotificationPrefs>;
  return {
    pushCourtDates: data.pushCourtDates !== false,
    emailSummaries: data.emailSummaries === true,
    marketingInApp: data.marketingInApp !== false,
  };
}

export async function fetchUserNotifications(
  userId: string,
): Promise<UserNotification[]> {
  const q = query(
    collection(db, 'user_notifications'),
    where('userId', '==', userId),
    limit(USER_NOTIFICATION_LIMIT),
  );

  try {
    const snap = await getDocs(q);
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as UserNotification);
    rows.sort(
      (a, b) => safeParseDate(b.createdAt).getTime() - safeParseDate(a.createdAt).getTime(),
    );
    return rows;
  } catch (error) {
    console.warn('[userNotifications] fetch', error);
    return [];
  }
}

export async function markNotificationRead(notificationId: string): Promise<void> {
  await updateDoc(doc(db, 'user_notifications', notificationId), {
    read: true,
    readAt: new Date().toISOString(),
  });
}

export async function dismissNotificationBanner(notificationId: string): Promise<void> {
  await updateDoc(doc(db, 'user_notifications', notificationId), {
    bannerDismissedAt: new Date().toISOString(),
  });
}

/** Newest unread notification that should auto-open as a banner on sign-in. */
export function pickAutoShowBannerNotification(
  notifications: UserNotification[],
): UserNotification | null {
  return notifications.find((n) => !n.read && !n.bannerDismissedAt) ?? null;
}

export async function markAllNotificationsRead(userId: string, notifications: UserNotification[]): Promise<void> {
  const unread = notifications.filter((n) => !n.read && n.userId === userId);
  await Promise.all(unread.map((n) => markNotificationRead(n.id)));
}

export function countUnread(notifications: UserNotification[]): number {
  return notifications.filter((n) => !n.read).length;
}
